import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { VehicleAlert } from '../../hooks/useVehicleAlerts';
import { isCriticalAlert } from '../../monitoringUtils';
import useFmText from '../useFmText';
import './AlertsTab.scss';

type AlertCategory = 'components' | 'brake' | 'fluid' | 'battery' | 'others';

interface AlertGroup {
    key: string;
    latest: VehicleAlert;
    items: VehicleAlert[];
    critical: boolean;
}

const CATEGORIES: { key: AlertCategory; icon: string; labelId: string }[] = [
    { key: 'components', icon: 'rioglyph-component-custom-recurrent', labelId: 'intl-msg:alerts.components' },
    { key: 'brake', icon: 'rioglyph-brake', labelId: 'intl-msg:alerts.brakes' },
    { key: 'fluid', icon: 'rioglyph-temperature', labelId: 'intl-msg:alerts.fluid' },
    { key: 'battery', icon: 'rioglyph-battery-level-full', labelId: 'intl-msg:alerts.battery' },
    { key: 'others', icon: 'rioglyph-exclamation', labelId: 'intl-msg:alerts.others' },
];

const KNOWN: AlertCategory[] = ['components', 'brake', 'fluid', 'battery'];

const categoryOf = (alert: VehicleAlert): AlertCategory => {
    const category = String(alert.category ?? '') as AlertCategory;
    return KNOWN.includes(category) ? category : 'others';
};

const value = (input: unknown) => (input === undefined || input === null || input === '' ? undefined : String(input));

const BR_DATE = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T,]+(\d{2}):(\d{2})(?::(\d{2}))?)?/;

const toTime = (date: unknown) => {
    const text = value(date);
    if (!text) {
        return Number.NaN;
    }
    const match = text.match(BR_DATE);
    if (match) {
        const [, day, month, year, hour = '0', minute = '0', second = '0'] = match;
        return new Date(+year, +month - 1, +day, +hour, +minute, +second).getTime();
    }
    return Date.parse(text);
};

const byNewest = (a: VehicleAlert, b: VehicleAlert) => {
    const diff = toTime(b.date) - toTime(a.date);
    return Number.isNaN(diff) ? 0 : diff;
};

const groupKey = (alert: VehicleAlert) => {
    const spn = value(alert.spn);
    const fmi = value(alert.fmi);
    if (!spn && !fmi) {
        return `desc|${value(alert.spnDescription) ?? value(alert.description) ?? ''}`;
    }
    return [value(alert.sourceAddress) ?? '', spn ?? '', fmi ?? ''].join('|');
};

const groupAlerts = (alerts: VehicleAlert[]): AlertGroup[] => {
    const map = new Map<string, VehicleAlert[]>();
    for (const alert of alerts) {
        const key = groupKey(alert);
        const items = map.get(key);
        if (items) {
            items.push(alert);
        } else {
            map.set(key, [alert]);
        }
    }
    return [...map.entries()]
        .map(([key, items]) => {
            const sorted = [...items].sort(byNewest);
            return { key, latest: sorted[0], items: sorted, critical: sorted.some(isCriticalAlert) };
        })
        .sort((a, b) => byNewest(a.latest, b.latest) || b.items.length - a.items.length);
};

interface IAlertsTabProps {
    alerts: VehicleAlert[];
    loading?: boolean;
}

const AlertsTab = ({ alerts, loading }: IAlertsTabProps) => {
    const intl = useIntl();
    const { t } = useFmText();
    const notAvailable = intl.formatMessage({ id: 'intl-msg:alerts.notAvailable' });
    const notInformed = intl.formatMessage({ id: 'intl-msg:alerts.notInformed' });
    const [category, setCategory] = useState<AlertCategory>('components');
    const [selected, setSelected] = useState<AlertGroup | null>(null);

    const byCategory = useMemo(() => {
        const map = new Map<AlertCategory, VehicleAlert[]>(CATEGORIES.map(item => [item.key, []]));
        for (const alert of alerts) {
            map.get(categoryOf(alert))?.push(alert);
        }
        return new Map([...map.entries()].map(([key, list]) => [key, groupAlerts(list)]));
    }, [alerts]);

    useEffect(() => {
        setSelected(null);
        setCategory(alerts.length ? categoryOf(alerts[0]) : 'components');
    }, [alerts]);

    if (loading) {
        return (
            <ul className='fm-alertcards'>
                {['a1', 'a2', 'a3'].map(key => (
                    <li key={key}>
                        <span className='fm-skel' style={{ width: '100%', height: 72 }} />
                    </li>
                ))}
            </ul>
        );
    }

    if (selected) {
        const alert = selected.latest;
        const count = selected.items.length;
        const rows: [string, string | undefined][] = [
            ['SA', value(alert.sourceAddressDescription) ?? value(alert.sourceAddress) ?? notInformed],
            [intl.formatMessage({ id: 'intl-msg:alerts.spnDescription' }), value(alert.spnDescription) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.fmiDescription' }), value(alert.fmiDescription) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.fmiCode' }), value(alert.fmi) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.spnCode' }), value(alert.spn) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.dateTime' }), value(alert.date) ?? notAvailable],
            [intl.formatMessage({ id: 'intl-msg:alerts.description' }), value(alert.description) ?? notAvailable],
            [
                intl.formatMessage({ id: 'intl-msg:alerts.status' }),
                value(alert.lampStatus) ?? value(alert.status) ?? notInformed,
            ],
        ];
        if (value(alert.km)) {
            rows.push([intl.formatMessage({ id: 'intl-msg:alerts.odometer' }), `${value(alert.km)} km`]);
        }
        rows.push([t('alerts.occurrencesLabel'), t('alerts.occurrencesTooltip', { count })]);
        return (
            <>
                <button type='button' className='fm-linkbtn fm-back' onClick={() => setSelected(null)}>
                    <span className='rioglyph rioglyph-arrow-left' />{' '}
                    {intl.formatMessage({ id: 'intl-msg:alerts.details' })}
                </button>
                <dl className='fm-dmeta fm-dmeta-spaced'>
                    {rows.map(([label, text]) => (
                        <div key={label} className='fm-dmeta-row'>
                            <dt>{label}</dt>
                            <dd>{text}</dd>
                        </div>
                    ))}
                </dl>
                {count > 1 && (
                    <>
                        <h3 className='fm-section-title'>{t('alerts.history')}</h3>
                        <ul className='fm-alerthist'>
                            {selected.items.map((item, index) => (
                                <li key={String(item.id ?? `${item.date}-${index}`)}>
                                    <span>{value(item.date) ?? notAvailable}</span>
                                    {value(item.km) && <small>{`${value(item.km)} km`}</small>}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </>
        );
    }

    const list = byCategory.get(category) ?? [];
    const current = CATEGORIES.find(item => item.key === category);

    return (
        <>
            <div className='fm-segtabs' role='tablist'>
                {CATEGORIES.map(item => {
                    const count = byCategory.get(item.key)?.length ?? 0;
                    return (
                        <button
                            key={item.key}
                            type='button'
                            role='tab'
                            aria-selected={category === item.key}
                            title={intl.formatMessage({ id: item.labelId })}
                            className='fm-segtab'
                            onClick={() => setCategory(item.key)}
                        >
                            <span className={`rioglyph ${item.icon}`} />
                            {count > 0 && <span className='fm-segtab__dot' />}
                        </button>
                    );
                })}
            </div>

            <h3 className='fm-section-title'>{current ? intl.formatMessage({ id: current.labelId }) : ''}</h3>

            {list.length ? (
                <ul className='fm-alertcards'>
                    {list.map(group => {
                        const alert = group.latest;
                        const count = group.items.length;
                        const oldest = group.items[count - 1];
                        return (
                            <li key={group.key}>
                                <button
                                    type='button'
                                    className={`fm-alertcard${group.critical ? ' fm-critical' : ''}`}
                                    onClick={() => setSelected(group)}
                                >
                                    <span className='rioglyph rioglyph-warning-sign fm-alertcard__icon' />
                                    <span className='fm-alertcard__body'>
                                        <span className='fm-alertcard__title'>
                                            <b>
                                                {value(alert.spnDescription) ??
                                                    value(alert.description) ??
                                                    value(alert.sourceAddress) ??
                                                    intl.formatMessage({ id: 'intl-msg:alerts.noDescription' })}
                                            </b>
                                            {count > 1 && (
                                                <span
                                                    className='fm-alertcard__count'
                                                    title={t('alerts.occurrencesTooltip', { count })}
                                                >
                                                    {t('alerts.occurrences', { count })}
                                                </span>
                                            )}
                                        </span>
                                        <small>
                                            SA:{' '}
                                            {value(alert.sourceAddressDescription) ??
                                                value(alert.sourceAddress) ??
                                                notAvailable}{' '}
                                            | SPN: {value(alert.spn) ?? notAvailable} | FMI:{' '}
                                            {value(alert.fmi) ?? notAvailable}
                                        </small>
                                        <small>{value(alert.fmiDescription) ?? notAvailable}</small>
                                        <small>
                                            {count > 1
                                                ? `${t('alerts.lastSeen', { date: value(alert.date) ?? notAvailable })} · ${t(
                                                      'alerts.firstSeen',
                                                      { date: value(oldest.date) ?? notAvailable }
                                                  )}`
                                                : (value(alert.date) ?? notAvailable)}
                                        </small>
                                    </span>
                                    <span className='rioglyph rioglyph-chevron-right fm-alertcard__chevron' />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className='fm-muted'>{intl.formatMessage({ id: 'intl-msg:alerts.noAlerts' })}</p>
            )}
        </>
    );
};

export default AlertsTab;
